import { drawRichTextLine, measureRichTextLine, measureRichTextLineMetrics } from "./richLabelText.js";

function cloneRichTextModel(richTextModel) {
  if (!richTextModel || !Array.isArray(richTextModel.lines)) {
    return undefined;
  }

  return JSON.parse(JSON.stringify(richTextModel));
}

function getRichTextLine(richTextModel, index) {
  if (!richTextModel || !Array.isArray(richTextModel.lines)) {
    return undefined;
  }

  if (!Object.prototype.hasOwnProperty.call(richTextModel.lines, index)) {
    return undefined;
  }

  return Array.isArray(richTextModel.lines[index]) ? richTextModel.lines[index] : undefined;
}

function getTextLineLayout(textArray, font, lineSpacing = 0) {
  const spacing = Number(lineSpacing) || 0;
  const lines = Array.isArray(textArray) ? textArray : [];
  const metrics = lines.map((text) => measureRichTextLineMetrics(text, font));
  const totalHeight = metrics.reduce((sum, metric, index) => (
    sum + metric.height + (index > 0 ? spacing : 0)
  ), 0);

  return {
    metrics,
    totalHeight,
    getYPositions(startY = 0) {
      let cursorY = startY;

      return metrics.map((metric, index) => {
        if (index > 0) {
          cursorY += spacing;
        }

        const y = cursorY + metric.ascent;
        cursorY = y + metric.descent;
        return y;
      });
    },
  };
}

function getCanvasImageScale(style = {}) {
  const imageScale = Number(style.imageScale);
  return !isNaN(imageScale) && imageScale > 0 ? imageScale : 1;
}

function getScaledImageDimensions(image, style = {}) {
  const width = image.naturalWidth || image.width || 0;
  const height = image.naturalHeight || image.height || 0;
  const imageScale = getCanvasImageScale(style);

  return {
    width: width * imageScale,
    height: height * imageScale,
  };
}

class LabelCanvasTexture {
  constructor(textArray, userStyle, richTextModel) {
    this.style = {
      backgroundColor: "#333333",
      color: "#FFFFFF",
      padding: 40,
      font: "90px Sans-Serif, Arial",
      textAlign: "center",
      lineSpacing: 20,
      borderRadius: 20,
    };
    this.textArray = Array.isArray(textArray) ? textArray : [];
    this.richText = cloneRichTextModel(richTextModel);
    this.style = Object.assign(this.style, userStyle);
    this.canvas = document.createElement("canvas");
    this.render();
  }

  getFontSize() {
    const explicitFontSize = Number(this.style.fontSize);
    if (!isNaN(explicitFontSize) && explicitFontSize > 0) {
      return explicitFontSize;
    }

    const fontMatch = String(this.style.font || "").match(/(\d+(?:\.\d+)?)px/);
    const fontSize = fontMatch ? Number(fontMatch[1]) : NaN;
    return !isNaN(fontSize) && fontSize > 0 ? fontSize : 90;
  }

  getFont() {
    const fontSize = this.getFontSize();
    const baseFont = this.style.font || "90px Sans-Serif, Arial";

    if (Object.prototype.hasOwnProperty.call(this.style, "fontSize")) {
      if (/(\d+(?:\.\d+)?)px/.test(baseFont)) {
        return baseFont.replace(/(\d+(?:\.\d+)?)px/, `${fontSize}px`);
      }

      return `${fontSize}px ${baseFont}`;
    }

    return baseFont;
  }

  getCanvas() {
    return this.canvas;
  }

  getRenderableLines() {
    const textLines = Array.isArray(this.textArray) ? this.textArray : [];
    const richLineCount = Array.isArray(this.richText?.lines) ? this.richText.lines.length : 0;
    const lineCount = Math.max(textLines.length, richLineCount);

    return Array.from({ length: lineCount }, (_, index) => getRichTextLine(this.richText, index) || textLines[index] || "");
  }

  render() {
    return new Promise((resolve, reject) => {
      const canvas = this.canvas;
      const ctx = canvas.getContext("2d");
      const renderLines = this.getRenderableLines();

      // Padding Numbers
      if (typeof this.style.padding === "string") {
        this.style.padding = parseInt(this.style.padding);
      }
      const paddingWidth = this.style.padding || 40; // Several old jobs have the padding explicitly set to 0. This fixes it to set it to 40.
      const paddingHeight = this.style.paddingHeight || 50; // Magic Number should this be configurable?
      const font = this.getFont();
      const fontSize = this.getFontSize();
      const lineSpacing = this.style.lineSpacing;
      const textLayout = getTextLineLayout(renderLines, font, lineSpacing);

      function drawRoundedRect(ctx, x, y, width, height, radius) {
        ctx.beginPath();
        ctx.moveTo(x + radius, y); // Move to the top-left corner
        ctx.lineTo(x + width - radius, y); // Top edge
        ctx.arcTo(x + width, y, x + width, y + radius, radius); // Top-right corner
        ctx.lineTo(x + width, y + height - radius); // Right edge
        ctx.arcTo(x + width, y + height, x + width - radius, y + height, radius); // Bottom-right corner
        ctx.lineTo(x + radius, y + height); // Bottom edge
        ctx.arcTo(x, y + height, x, y + height - radius, radius); // Bottom-left corner
        ctx.lineTo(x, y + radius); // Left edge
        ctx.arcTo(x, y, x + radius, y, radius); // Top-left corner
        ctx.closePath();
      }

      let imgHeight = 0;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (this.style.image) {
        const img = new window.Image();
        img.crossOrigin = "Anonymous";
        img.src = this.style.image;
        img.onload = () => {
          const imageDimensions = getScaledImageDimensions(img, this.style);
          imgHeight = imageDimensions.height;

          let maxWidth = 0;
          ctx.font = font;
          renderLines.map((line) => {
            const tempWidth = measureRichTextLine(ctx, line, font);
            if (tempWidth > maxWidth) {
              maxWidth = tempWidth;
            }
          });

          const textHeight = textLayout.totalHeight;
          ctx.canvas.width = Math.max(imageDimensions.width, maxWidth) + paddingWidth;
          ctx.canvas.height = imgHeight + textHeight + paddingHeight;

          ctx.fillStyle = this.style.backgroundColor;

          const x = 0;
          const y = 0;
          const width = ctx.canvas.width;
          const height = ctx.canvas.height;
          const radius = this.style.borderRadius || 20;

          drawRoundedRect(ctx, x, y, width, height, radius);
          ctx.fill();

          ctx.drawImage(
            img,
            (canvas.width - imageDimensions.width) / 2,
            textHeight + paddingHeight / 2,
            imageDimensions.width,
            imageDimensions.height
          );

          ctx.font = font;
          ctx.textAlign = this.style.textAlign;
          ctx.textBaseline = "middle";
          const lineYPositions = textLayout.getYPositions(paddingHeight / 2);
          for (let i = 0; i < renderLines.length; i++) {
            ctx.fillStyle = this.style.color;
            const xPos = canvas.width / 2;
            const yPos = lineYPositions[i];
            drawRichTextLine(ctx, renderLines[i], xPos, yPos, {
              font,
              fillStyle: this.style.color,
              textAlign: this.style.textAlign,
            });
          }
          resolve();
        };
      } else {
        let maxWidth = 0;
        ctx.font = font;

        renderLines.map((line) => {
          const tempWidth = measureRichTextLine(ctx, line, font);
          if (tempWidth > maxWidth) {
            maxWidth = tempWidth;
          }
        });

        const extraBuffer = fontSize * 0.2;
        const totalTextHeight = textLayout.totalHeight;

        ctx.canvas.width = maxWidth + paddingWidth + extraBuffer;
        ctx.canvas.height = totalTextHeight + paddingHeight + extraBuffer;

        // Fill Background Color
        ctx.fillStyle = this.style.backgroundColor;
        const x = 0;
        const y = 0;
        const width = ctx.canvas.width;
        const height = ctx.canvas.height;
        const radius = this.style.borderRadius || 20;

        // Draw the rounded rectangle
        drawRoundedRect(ctx, x, y, width, height, radius);
        ctx.fill();

        // Drop Shadow
        if (this.style.dropShadow) {
          ctx.shadowOffsetX = 5;
          ctx.shadowOffsetY = 5;
          ctx.shadowColor = "rgba(0, 0, 0, 0.8)";
          ctx.shadowBlur = 5;
        }

        ctx.font = font;
        ctx.textAlign = this.style.textAlign;
        ctx.textBaseline = "middle";

        const totalContentHeight = totalTextHeight;
        const startY = (ctx.canvas.height - totalContentHeight) / 2;
        const lineYPositions = textLayout.getYPositions(startY);
        for (var i = 0; i < renderLines.length; i++) {
          ctx.fillStyle = this.style.color;
          let xPos;
          const textAlign = this.style.textAlign || this.style.fontAlign || "center";

          switch (textAlign) {
            case "left":
              xPos = paddingWidth / 2;
              break;
            case "right":
              xPos = ctx.canvas.width - paddingWidth / 2;
              break;
            case "center":
            default:
              xPos = ctx.canvas.width / 2;
              break;
          }
          let yPos = lineYPositions[i];
          drawRichTextLine(ctx, renderLines[i], xPos, yPos, {
            font,
            fillStyle: this.style.color,
            textAlign,
          });
        }
        resolve();
      }
    });
  }

  setStyle(newStyle) {
    if (
      newStyle &&
      Object.prototype.hasOwnProperty.call(newStyle, "font") &&
      !Object.prototype.hasOwnProperty.call(newStyle, "fontSize")
    ) {
      const fontMatch = String(newStyle.font || "").match(/(\d+(?:\.\d+)?)px/);
      if (fontMatch) {
        newStyle.fontSize = Number(fontMatch[1]);
      }
    }
    this.style = Object.assign(this.style, newStyle);
    this.render();
  }

  removeStyle(key) {
    if (key) {
      delete this.style[key];
    }
    this.render();
  }

  replaceText(textArray, richTextModel) {
    this.textArray = Array.isArray(textArray) ? textArray : [];
    this.richText = cloneRichTextModel(richTextModel);
    this.render();
  }

  addText(textLine) {
    this.richText = undefined;
    this.textArray.push(textLine);
    this.render();
  }

  editText(textID, textLine) {
    if (this.textArray[textID] !== undefined) {
      this.richText = undefined;
      this.textArray[textID] = textLine;
    }
    this.render();
  }
}

export { LabelCanvasTexture, getTextLineLayout, getCanvasImageScale, getScaledImageDimensions };
